/**
 * How loud the score is.
 *
 * Nothing plays through this bus yet. It ships anyway, because a player who wants music off wants
 * it off before the first bar rather than after it, and because the test tone makes it a control
 * they can actually set rather than a promise.
 *
 * Writes to [the mix levels](../../../audio/mixLevels.ts), which the bus tree reads and subscribes
 * to, so a drag is heard while the thumb is still down and a level set before any audio context
 * exists is applied to the tree the moment one does.
 *
 * The bounds, the default and the curve between a slider and a gain are that module's, not this
 * file's. A knob names a quantity and hands a value to whoever owns it, and four copies of a
 * loudness curve is four chances for one of them to be wrong.
 */

import { MAX_MIX_LEVEL, MIN_MIX_LEVEL, DEFAULT_MIX_LEVELS, setMixLevel } from '@/audio/mixLevels';
import { rangeApply } from '@/ui/settings/controls/rangeSlider';
import { SETTING_MUSIC_LEVEL_LABEL, SETTING_MUSIC_LEVEL_HINT } from '@/text/settingsText';
import type { SettingDefinition } from '@/ui/settings/settingContract';

/** Five percent a step: fine enough to find a level, coarse enough to land on one again. */
const STEP = 0.05;

export const MUSIC_LEVEL: SettingDefinition = {
  id: 'musicLevel',
  section: 'audio',
  label: SETTING_MUSIC_LEVEL_LABEL,
  hint: SETTING_MUSIC_LEVEL_HINT,
  control: {
    kind: 'level',
    min: MIN_MIX_LEVEL,
    max: MAX_MIX_LEVEL,
    step: STEP,
    // A percentage, because a volume is a share of full and 0.65 is a number nobody can picture.
    format: (value: number): string => `${String(Math.round(value * 100))}%`,
    // Imported at the moment it is pressed, never at the top of the file. The settings registry is
    // reachable from the menu, so a static import here would put the audio engine and everything it
    // reaches into the chunk that draws the cards, for a player who has not opened settings.
    audition: (): void => {
      void import('@/audio/testTone').then((module) => {
        module.playTestTone('music');
      });
    },
  },
  defaultValue: DEFAULT_MIX_LEVELS.music,
  apply: rangeApply((value: number): void => {
    setMixLevel('music', value);
  }),
};
